/**
 * 입력 커맨드 — 결정론의 3번 규칙.
 *
 * 클라이언트가 서버로 보내는 건 상태가 아니라 이 목록뿐이다.
 * 모든 필드는 정수여야 하고, 마우스 좌표 같은 연속값은 여기서 양자화된다.
 * 커맨드는 항상 (tick, seq) 오름차순으로 정렬된 상태로 적용한다.
 */

export const CmdType = {
  /** 장수 이동 입력. a, b ∈ {-1, 0, 1} (8방향). */
  MoveDir: 0,
  /** 선택된 분대에 명령을 내린다. a: Order */
  Order: 1,
  /**
   * 선택된 분대의 이동경로를 새로 시작한다. a, b = 첫 지점의 픽셀 정수 좌표.
   *
   * 옛 이름은 군기였다. 군기는 꽂히면 부대가 거기 서서 지켰다. 이제는 경로의
   * 첫 점이고, 뒤에 `Waypoint`가 이어질 수 있다. 끝점에 닿으면 부대는 제 명령으로
   * 돌아간다 — 기병을 돌려 적 뒤를 치게 하고 나서 다시 손댈 필요가 없어야 한다.
   */
  Rally: 2,
  /** 장수 휩쓸기 시도(쿨다운은 시뮬레이션이 관리). */
  Strike: 3,
  /**
   * 분대 선택. a = 비트 마스크. 1번 비트가 0번 분대다.
   *
   * 명령마다 분대 번호를 싣는 대신 선택 상태를 시뮬레이션이 들고 있게 한다.
   * 커맨드 구조를 넓히지 않아도 되고, 실시간 전투에서 숫자키로 고르고
   * 우클릭으로 찍는 흐름이 그대로 나온다.
   */
  SelectSquad: 4,
  /** 선택된 분대의 이동경로를 지운다. 곧장 제 명령으로 돌아간다. */
  Recall: 5,
  /** 선택된 분대의 이동경로 끝에 지점을 하나 더한다. a, b = 픽셀 정수 좌표. */
  Waypoint: 6,
  /** 장수의 함성 시도(재사용 대기는 시뮬레이션이 관리). `World.leaderRoar`. */
  Roar: 7,
} as const;
export type CmdType = (typeof CmdType)[keyof typeof CmdType];

/** 분대 최대 수. 장수 직할대 하나와 동료 셋이다. */
export const MAX_SQUADS = 4;
/** 이동경로 한 개의 최대 지점 수. 화면이 끄는 선을 이 수까지 솎아 보낸다. */
export const MAX_WAYPOINTS = 16;
export const ALL_SQUADS = (1 << MAX_SQUADS) - 1;

/**
 * 부대 명령. 부대의 지휘관이 무엇을 할지 정하고, 부대원은 그에 맞춰 움직인다.
 *
 * 예전에는 태세(추종·돌격·사수)였다. 태세는 병사가 무엇을 할지만 정했고 장수는
 * 플레이어가 누른 만큼만 걸었다. 목표 플레이어를 "지휘하는 사람"으로 정하면서
 * 장수도 스스로 싸우게 했고, 그러자 명령이 지휘관의 행동까지 정해야 했다.
 * 값의 자리(0~2)는 옛 태세와 겹치지만 뜻이 다르다. 리플레이 버전으로 가른다.
 */
export const Order = {
  /** 지휘관이 제 걸음으로 적을 찾아 들어가고 부대원은 따라가며 적극적으로 친다. */
  Attack: 0,
  /**
   * REPLAY_VERSION 30~107의 기본(108부터 기본은 공격이다). 부대원은 지휘관 곁에 진을 짜고 진 가까이 온 적만 나가서 친다. 손을 놓은
   * 장수는 아무도 싸우지 않고 적이 가까이(450px) 보이지 않는 동안 가장 느린 병사 걸음으로 진을 끌고
   * 적 쪽으로 나아간다 — 부대장들의 자동전투. 예전에는 선 자리에서 기다리기만 했다.
   */
  Formation: 1,
  /** 선 자리에서 멈춘다. 닿는 적만 친다. */
  Hold: 2,
  /** 출발 쪽 가장자리로 물러난다. 닿으면 전장을 빠져나간다. 장수는 남는다. */
  Retreat: 3,
} as const;
export type Order = (typeof Order)[keyof typeof Order];

/**
 * 분대 이름. 숫자를 쓰지 않는 이유는 선택 키의 번호와 겹쳐 헷갈리기 때문이다.
 * 2번 키가 1분대를 고르는 화면은 읽는 사람을 매번 한 박자 멈추게 한다.
 */
export const SQUAD_NAMES: readonly string[] = ['직할', '갑대', '을대', '병대'];

export const ORDER_NAMES: Record<Order, string> = {
  [Order.Attack]: '공격',
  [Order.Formation]: '대형',
  [Order.Hold]: '대기',
  [Order.Retreat]: '후퇴',
};

export interface Command {
  readonly tick: number;
  readonly type: CmdType;
  readonly a: number;
  readonly b: number;
}

export function cmd(tick: number, type: CmdType, a = 0, b = 0): Command {
  return { tick, type, a: a | 0, b: b | 0 };
}

/**
 * 틱별 커맨드 로그. 기록과 재생 양쪽에서 같은 자료구조를 쓴다.
 * 배열 하나에 모아두고 커서로 훑는다 — 맵을 쓰면 순회 순서가 엔진에 의존한다.
 */
export class CommandLog {
  private readonly items: Command[] = [];
  private cursor = 0;

  push(c: Command): void {
    const last = this.items[this.items.length - 1];
    if (last && last.tick > c.tick) {
      throw new Error(`커맨드가 시간 역순으로 들어왔습니다: ${last.tick} → ${c.tick}`);
    }
    this.items.push(c);
  }

  /** 해당 틱의 커맨드들을 순서대로 돌려준다. 커서는 앞으로만 간다. */
  take(tick: number, out: Command[]): number {
    out.length = 0;
    while (this.cursor < this.items.length && this.items[this.cursor].tick === tick) {
      out.push(this.items[this.cursor]);
      this.cursor++;
    }
    // 지나간 틱의 커맨드가 남아 있으면 버린다(지각 입력).
    while (this.cursor < this.items.length && this.items[this.cursor].tick < tick) {
      this.cursor++;
    }
    return out.length;
  }

  rewind(): void {
    this.cursor = 0;
  }

  get length(): number {
    return this.items.length;
  }

  toJSON(): number[] {
    const flat: number[] = [];
    for (const c of this.items) flat.push(c.tick, c.type, c.a, c.b);
    return flat;
  }

  /**
   * 바깥에서 들어온 커맨드는 정수로 강제한다.
   *
   * `cmd()`를 지나온 값은 이미 정수지만, 리플레이는 서버나 파일에서 오는 날 값이다.
   * 소수가 섞이면 그대로 `inputDx` 같은 시뮬레이션 상태에 앉아 부동소수점 상태
   * 금지 규칙이 조용히 깨진다. 정상적인 리플레이는 이 강제에 걸리지 않는다.
   */
  static fromJSON(flat: readonly number[]): CommandLog {
    const log = new CommandLog();
    for (let i = 0; i + 3 < flat.length; i += 4) {
      log.push(cmd(flat[i] | 0, (flat[i + 1] | 0) as CmdType, flat[i + 2], flat[i + 3]));
    }
    return log;
  }
}
